import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Crop as CropIcon, Pause, Play } from 'lucide-react';
import { ANIM_LOOPS, FILMSTRIP, encodePreset, presetEncode, type AnimLoop, type EncodeSettings } from '@vidmakr/shared';
import { Button, Field, Input, Select, cx } from '@/components/ui';

/** Shared parts of the two GIF makers: from a video in the library (GifDialog) and from the timeline (TimelineGifDialog). */

export const US = 1_000_000;
export const GIF = presetEncode(encodePreset('gif_preview')!);
/** Quick picks for the GIF's short side. */
export const GIF_SIZES = [240, 320, 480, 720];
export const GIF_FPS = [10, 12, 15, 20, 25];
/** Longer GIFs get very large: say so. */
export const LONG_GIF_US = 15 * US;
/** Shortest GIF. */
export const MIN_GIF_US = 100_000;
/** Length of a new range when nothing is marked yet. */
export const DEFAULT_GIF_US = 3 * US;

/** "1:04.25" (minutes, seconds, hundredths). */
export function formatClipTime(us: number): string {
  const cs = Math.max(0, Math.round(us / 10_000));
  const m = Math.floor(cs / 6000);
  const s = Math.floor((cs % 6000) / 100);
  return `${m}:${String(s).padStart(2, '0')}.${String(cs % 100).padStart(2, '0')}`;
}

/** "1:04.25", "64.25" or "1:04" → µs; null when unreadable. */
export function parseClipTime(text: string): number | null {
  const m = /^\s*(?:(\d+):)?(\d+(?:[.,]\d*)?)\s*$/.exec(text);
  if (!m) return null;
  return Math.round(((Number(m[1] ?? 0) * 60) + Number(m[2].replace(',', '.'))) * US);
}

/** What the range editor plays and scrubs: a <video> or the editor's player. */
export interface RangeMedia {
  durationUs: number;
  timeUs: number;
  playing: boolean;
  fps: number;
  seek(us: number): void;
  /** Plays from the start of the range, looping it. */
  playRange(): void;
  pause(): void;
  step(frames: number): void;
}

/** A filmstrip sprite sheet behind the track (library videos have one). */
export interface Filmstrip {
  url: string;
  tiles: number;
  intervalUs: number;
}

type Drag = { mode: 'start' | 'end' | 'move' | 'seek'; grabUs: number; startUs: number; endUs: number };

/**
 * Marks the part that becomes the GIF: drag the two handles (the picture
 * follows the handle), drag the highlighted part to move it, click anywhere
 * else to look there. I and O set the start and end at the playhead, Space
 * plays the range on a loop, the arrows step a frame (`keys`, where nothing
 * else handles them). The times can also be typed.
 */
export function RangeEditor({
  media,
  startUs,
  endUs,
  onChange,
  filmstrip,
  keys = false,
}: {
  media: RangeMedia;
  startUs: number;
  endUs: number;
  onChange: (startUs: number, endUs: number) => void;
  filmstrip?: Filmstrip | null;
  keys?: boolean;
}) {
  const track = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const [dragging, setDragging] = useState<Drag['mode'] | null>(null);
  const dur = Math.max(1, media.durationUs);
  const frame = US / Math.max(1, media.fps);
  const snap = (us: number) => Math.max(0, Math.min(dur, Math.round(Math.round(us / frame) * frame)));
  const setStart = (us: number) => {
    const s = snap(Math.min(us, dur - MIN_GIF_US));
    onChange(s, endUs - s < MIN_GIF_US ? Math.min(dur, s + DEFAULT_GIF_US) : endUs);
  };
  const setEnd = (us: number) => {
    const e = snap(Math.max(us, MIN_GIF_US));
    onChange(e - startUs < MIN_GIF_US ? Math.max(0, e - DEFAULT_GIF_US) : startUs, e);
  };
  // Latest values for the key handler.
  const live = useRef({ media, setStart, setEnd });
  live.current = { media, setStart, setEnd };

  useEffect(() => {
    if (!keys) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('input, textarea, select, [contenteditable=true]') || e.metaKey || e.ctrlKey || e.altKey) return;
      const { media: m, setStart: ss, setEnd: se } = live.current;
      const key = e.key.toLowerCase();
      if (key === 'i') ss(m.timeUs);
      else if (key === 'o') se(m.timeUs);
      else if (key === ' ') (m.playing ? m.pause() : m.playRange());
      else if (key === 'arrowleft') m.step(e.shiftKey ? -10 : -1);
      else if (key === 'arrowright') m.step(e.shiftKey ? 10 : 1);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [keys]);

  const usAt = (clientX: number) => {
    const r = track.current!.getBoundingClientRect();
    return ((clientX - r.left) / Math.max(1, r.width)) * dur;
  };
  const onDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const mode = ((e.target as HTMLElement).closest('[data-range-part]')?.getAttribute('data-range-part') ?? 'seek') as Drag['mode'];
    drag.current = { mode, grabUs: usAt(e.clientX), startUs, endUs };
    setDragging(mode);
    e.currentTarget.setPointerCapture(e.pointerId);
    if (media.playing) media.pause();
    if (mode === 'seek') media.seek(snap(usAt(e.clientX)));
    e.preventDefault();
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const at = usAt(e.clientX);
    if (d.mode === 'seek') media.seek(snap(at));
    else if (d.mode === 'start') {
      // Relative to where the handle was grabbed, so it does not jump to the pointer.
      const s = snap(Math.max(0, Math.min(d.startUs + at - d.grabUs, d.endUs - MIN_GIF_US)));
      onChange(s, d.endUs);
      media.seek(s);
    } else if (d.mode === 'end') {
      const en = snap(Math.min(dur, Math.max(d.endUs + at - d.grabUs, d.startUs + MIN_GIF_US)));
      onChange(d.startUs, en);
      media.seek(en);
    } else {
      const len = d.endUs - d.startUs;
      const s = snap(Math.max(0, Math.min(dur - len, d.startUs + at - d.grabUs)));
      onChange(s, s + len);
      media.seek(s);
    }
  };
  const onUp = () => {
    drag.current = null;
    setDragging(null);
  };

  const pct = (us: number) => (Math.max(0, Math.min(us, dur)) / dur) * 100;
  const lengthUs = endUs - startUs;
  return (
    <div className="space-y-3" data-gif-range>
      <div
        ref={track}
        className={cx('relative h-14 touch-none select-none overflow-hidden rounded-lg bg-panel-3', dragging === 'move' ? 'cursor-grabbing' : 'cursor-pointer')}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        {filmstrip ? <FilmstripTiles strip={filmstrip} durUs={dur} /> : <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,transparent_0,transparent_calc(10%-1px),rgb(255_255_255/0.06)_calc(10%-1px),rgb(255_255_255/0.06)_10%)]" />}
        {/* Outside the range: dimmed. */}
        <div className="pointer-events-none absolute inset-y-0 left-0 bg-black/60" style={{ width: `${pct(startUs)}%` }} />
        <div className="pointer-events-none absolute inset-y-0 right-0 bg-black/60" style={{ width: `${100 - pct(endUs)}%` }} />
        {/* The range: drag it to move it, its edges to resize it. */}
        <div
          data-range-part="move"
          title="Drag to move the selection"
          className={cx('absolute inset-y-0 border-y-[3px] border-accent', dragging === 'move' ? 'cursor-grabbing' : 'cursor-grab')}
          style={{ left: `${pct(startUs)}%`, width: `${pct(endUs) - pct(startUs)}%` }}
        />
        <Handle part="start" left={pct(startUs)} title="Drag to set where the GIF starts (I)" />
        <Handle part="end" left={pct(endUs)} title="Drag to set where the GIF ends (O)" />
        <div className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_2px_rgb(0_0_0/0.8)]" style={{ left: `${pct(media.timeUs)}%` }}>
          <div className="absolute -top-px left-1/2 h-0 w-0 -translate-x-1/2 border-x-[5px] border-t-[6px] border-x-transparent border-t-white" />
        </div>
      </div>
      <div className="-mt-2 flex justify-between font-mono text-[10px] tabular-nums text-faint">
        <span>{formatClipTime(0)}</span>
        <span>{formatClipTime(dur / 2)}</span>
        <span>{formatClipTime(dur)}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant={media.playing ? 'secondary' : 'primary'} onClick={() => (media.playing ? media.pause() : media.playRange())} data-gif-play>
          {media.playing ? <Pause size={14} /> : <Play size={14} />} {media.playing ? 'Pause' : 'Play the GIF part'}
        </Button>
        <div className="flex items-center">
          <Button size="icon-sm" variant="ghost" onClick={() => media.step(-1)} aria-label="Previous frame" title="Previous frame (←)">
            <ChevronLeft size={16} />
          </Button>
          <span className="w-16 text-center font-mono text-xs tabular-nums text-muted">{formatClipTime(media.timeUs)}</span>
          <Button size="icon-sm" variant="ghost" onClick={() => media.step(1)} aria-label="Next frame" title="Next frame (→)">
            <ChevronRight size={16} />
          </Button>
        </div>
        <div className="flex-1" />
        <Button size="sm" onClick={() => setStart(media.timeUs)} data-gif-set-start title="Start the GIF at the playhead (I)">
          Start here <Kbd>I</Kbd>
        </Button>
        <Button size="sm" onClick={() => setEnd(media.timeUs)} data-gif-set-end title="End the GIF at the playhead (O)">
          End here <Kbd>O</Kbd>
        </Button>
      </div>

      <div className="grid grid-cols-3 items-end gap-3">
        <Field label="Start">
          <TimeInput valueUs={startUs} onCommit={(us) => (setStart(us), media.seek(snap(us)))} data-gif-start />
        </Field>
        <div className="pb-2 text-center text-xs text-muted">
          <span className="font-medium text-fg">{(lengthUs / US).toFixed(2)} s</span> long
        </div>
        <Field label="End">
          <TimeInput valueUs={endUs} onCommit={(us) => (setEnd(us), media.seek(snap(us)))} data-gif-end />
        </Field>
      </div>
      <p className="text-xs text-faint">Drag the handles or the highlighted part, or play and press I at the start and O at the end. Space plays the GIF part on a loop.</p>
    </div>
  );
}

function Handle({ part, left, title }: { part: 'start' | 'end'; left: number; title: string }) {
  return (
    <div
      data-range-part={part}
      title={title}
      className={cx(
        'absolute inset-y-0 z-10 flex w-3.5 cursor-ew-resize items-center justify-center bg-accent hover:brightness-110',
        part === 'start' ? 'rounded-l-md' : '-translate-x-full rounded-r-md',
      )}
      style={{ left: `${left}%` }}
    >
      <div className="h-5 w-0.5 rounded-full bg-white/80" />
    </div>
  );
}

function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="ml-1 rounded border border-line bg-panel-2 px-1 font-mono text-[10px] text-muted">{children}</kbd>;
}

/** A time field: shows "m:ss.cc", takes that or plain seconds; applied on Enter or when leaving it. */
function TimeInput({ valueUs, onCommit, ...rest }: { valueUs: number; onCommit: (us: number) => void } & Record<`data-${string}`, unknown>) {
  const [text, setText] = useState<string | null>(null);
  const commit = () => {
    if (text === null) return;
    const us = parseClipTime(text);
    setText(null);
    if (us !== null) onCommit(us);
  };
  return (
    <Input
      className="font-mono tabular-nums"
      value={text ?? formatClipTime(valueUs)}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') {
          e.stopPropagation();
          setText(null);
        }
      }}
      {...rest}
    />
  );
}

/** The filmstrip spread over the track: one tile per slot, each the frame nearest that time. */
function FilmstripTiles({ strip, durUs }: { strip: Filmstrip; durUs: number }) {
  const slots = 12;
  const rows = Math.ceil(strip.tiles / FILMSTRIP.columns);
  return (
    <div className="pointer-events-none absolute inset-0 flex">
      {Array.from({ length: slots }, (_, i) => {
        const t = ((i + 0.5) / slots) * durUs;
        const tile = Math.min(strip.tiles - 1, Math.max(0, Math.floor(t / Math.max(1, strip.intervalUs))));
        return (
          <div
            key={i}
            className="h-full flex-1 bg-no-repeat"
            style={{
              backgroundImage: `url(${strip.url})`,
              backgroundSize: `${FILMSTRIP.columns * 100}% ${rows * 100}%`,
              backgroundPosition: `${((tile % FILMSTRIP.columns) / Math.max(1, FILMSTRIP.columns - 1)) * 100}% ${rows > 1 ? (Math.floor(tile / FILMSTRIP.columns) / (rows - 1)) * 100 : 0}%`,
            }}
          />
        );
      })}
    </div>
  );
}

/** The part of the picture kept, as fractions of the frame. */
export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}
const FULL: CropRect = { x: 0, y: 0, w: 1, h: 1 };
/** Shapes the crop can be held to (width / height in pixels); null = free. */
export const CROP_SHAPES: { id: string; label: string; ratio: number | null }[] = [
  { id: 'free', label: 'Free', ratio: null },
  { id: 'original', label: 'Original', ratio: null },
  { id: '1:1', label: 'Square 1:1', ratio: 1 },
  { id: '4:5', label: 'Portrait 4:5', ratio: 4 / 5 },
  { id: '9:16', label: 'Vertical 9:16', ratio: 9 / 16 },
  { id: '16:9', label: 'Wide 16:9', ratio: 16 / 9 },
];
const MIN_SIDE = 16;
const MAX_SIDE = 3840;
const even = (v: number) => Math.max(2, Math.round(v / 2) * 2);
const clampSide = (v: number) => Math.max(MIN_SIDE, Math.min(MAX_SIDE, even(v)));

/** The largest crop of a pixel shape (width / height) centred in a frame of `frameAspect`. */
function centredCrop(ratio: number, frameAspect: number): CropRect {
  const w = Math.min(1, ratio / frameAspect);
  const h = Math.min(1, frameAspect / ratio);
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
}

/**
 * Size and crop of a GIF: the crop (off = the whole frame) sets its shape,
 * the size its pixels (typed as width or height; the other follows the shape).
 */
export function useGifShape(srcW: number, srcH: number) {
  const frameAspect = Math.max(1, srcW) / Math.max(1, srcH);
  const [cropOn, setCropOn] = useState(false);
  const [shapeId, setShapeId] = useState('free');
  const [rect, setRect] = useState<CropRect>(FULL);
  const [shortSide, setShortSide] = useState(() => Math.min(320, even(Math.min(srcW, srcH) || 320)));
  const kept = cropOn ? rect : FULL;
  const aspect = (kept.w * frameAspect) / Math.max(1e-6, kept.h);
  const short = clampSide(shortSide);
  const width = aspect >= 1 ? clampSide(short * aspect) : short;
  const height = aspect >= 1 ? short : clampSide(short / aspect);
  const ratio = shapeId === 'original' ? frameAspect : (CROP_SHAPES.find((c) => c.id === shapeId)?.ratio ?? null);
  return {
    frameAspect,
    cropOn,
    setCropOn: (on: boolean) => {
      setCropOn(on);
      if (on && rect === FULL) setRect(ratio ? centredCrop(ratio, frameAspect) : { x: 0.1, y: 0.1, w: 0.8, h: 0.8 });
    },
    shapeId,
    setShape: (id: string) => {
      setShapeId(id);
      const r = id === 'original' ? frameAspect : CROP_SHAPES.find((c) => c.id === id)?.ratio;
      if (r) setRect(centredCrop(r, frameAspect));
    },
    /** Width / height in pixels the crop is held to, or null. */
    ratio,
    rect: kept,
    setRect,
    resetCrop: () => {
      setRect(FULL);
      setShapeId('free');
      setCropOn(false);
    },
    width,
    height,
    setWidth: (w: number) => setShortSide(aspect >= 1 ? w / aspect : w),
    setHeight: (h: number) => setShortSide(aspect >= 1 ? h : h * aspect),
    setShortSide,
    /** How many source pixels the kept part has (to warn about blowing it up). */
    keptPixels: { width: Math.round(kept.w * srcW), height: Math.round(kept.h * srcH) },
    /** The encode's crop, when part of the frame is cut away. */
    crop: cropOn && (kept.w < 0.999 || kept.h < 0.999) ? { ...kept, width, height } : undefined,
  };
}
export type GifShape = ReturnType<typeof useGifShape>;

/**
 * A box holding the picture at its own shape (as large as fits), with the
 * crop drawn over it when cropping: drag inside to move it, the corners and
 * edges to resize it (held to the chosen shape).
 */
export function PictureFrame({ shape, maxHeight = '45vh', children, onClick }: { shape: GifShape; maxHeight?: string; children: ReactNode; onClick?: () => void }) {
  return (
    <div
      className="relative mx-auto overflow-hidden rounded-lg bg-black"
      style={{ aspectRatio: `${shape.frameAspect}`, width: `min(100%, calc(${maxHeight} * ${shape.frameAspect}))` }}
      onClick={shape.cropOn ? undefined : onClick}
    >
      {children}
      {shape.cropOn ? <CropBox shape={shape} /> : null}
    </div>
  );
}

type CropDrag = { mode: string; px: number; py: number; start: CropRect; box: DOMRect };

function CropBox({ shape }: { shape: GifShape }) {
  const drag = useRef<CropDrag | null>(null);
  const { rect, ratio, frameAspect } = shape;
  const MIN = 0.05;
  const onDown = (e: React.PointerEvent) => {
    const mode = (e.target as HTMLElement).closest('[data-crop-part]')?.getAttribute('data-crop-part');
    if (!mode || e.button !== 0) return;
    drag.current = { mode, px: e.clientX, py: e.clientY, start: rect, box: e.currentTarget.getBoundingClientRect() };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
    e.stopPropagation();
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.px) / d.box.width;
    const dy = (e.clientY - d.py) / d.box.height;
    const s = d.start;
    if (d.mode === 'move') {
      shape.setRect({ ...s, x: Math.max(0, Math.min(1 - s.w, s.x + dx)), y: Math.max(0, Math.min(1 - s.h, s.y + dy)) });
      return;
    }
    // Edges and corners: the opposite side stays put.
    let left = s.x;
    let top = s.y;
    let right = s.x + s.w;
    let bottom = s.y + s.h;
    if (d.mode.includes('w')) left = Math.max(0, Math.min(right - MIN, left + dx));
    if (d.mode.includes('e')) right = Math.min(1, Math.max(left + MIN, right + dx));
    if (d.mode.includes('n')) top = Math.max(0, Math.min(bottom - MIN, top + dy));
    if (d.mode.includes('s')) bottom = Math.min(1, Math.max(top + MIN, bottom + dy));
    let w = right - left;
    let h = bottom - top;
    if (ratio) {
      // Held to a shape: the dragged dimension leads (width for corners), the other follows, within the frame.
      const hFor = (ww: number) => (ww * frameAspect) / ratio;
      const wFor = (hh: number) => (hh * ratio) / frameAspect;
      const vertical = d.mode === 'n' || d.mode === 's';
      if (vertical) w = wFor(h);
      else h = hFor(w);
      const maxW = d.mode.includes('w') ? right : vertical ? 1 : 1 - left;
      const maxH = d.mode.includes('n') ? bottom : d.mode.includes('s') || !vertical ? 1 - top : 1;
      const k = Math.min(1, maxW / w, maxH / h);
      w *= k;
      h *= k;
      if (d.mode.includes('w')) left = right - w;
      if (d.mode.includes('n')) top = bottom - h;
      // Edge drags keep the box centred across.
      if (vertical) left = Math.max(0, Math.min(1 - w, s.x + (s.w - w) / 2));
      if (d.mode === 'e' || d.mode === 'w') top = Math.max(0, Math.min(1 - h, s.y + (s.h - h) / 2));
    }
    shape.setRect({ x: left, y: top, w, h });
  };
  const onUp = () => {
    drag.current = null;
  };
  const pct = (v: number) => `${v * 100}%`;
  const handles = ratio ? ['nw', 'ne', 'sw', 'se'] : ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
  return (
    <div className="absolute inset-0 touch-none" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} data-crop>
      <div
        data-crop-part="move"
        className="absolute cursor-move border-2 border-white shadow-[0_0_0_9999px_rgb(0_0_0/0.55)]"
        style={{ left: pct(rect.x), top: pct(rect.y), width: pct(rect.w), height: pct(rect.h) }}
      >
        {/* Thirds, to help placing. */}
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,transparent_33.2%,rgb(255_255_255/0.35)_33.3%,transparent_33.4%,transparent_66.6%,rgb(255_255_255/0.35)_66.7%,transparent_66.8%),linear-gradient(to_bottom,transparent_33.2%,rgb(255_255_255/0.35)_33.3%,transparent_33.4%,transparent_66.6%,rgb(255_255_255/0.35)_66.7%,transparent_66.8%)]" />
        {handles.map((h) => (
          <div
            key={h}
            data-crop-part={h}
            className="absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-sm border border-black/40 bg-white"
            style={{
              left: h.includes('w') ? '0%' : h.includes('e') ? '100%' : '50%',
              top: h.includes('n') ? '0%' : h.includes('s') ? '100%' : '50%',
              cursor: `${h}-resize`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

/** Crop, size, frame rate and looping of the GIF. */
export function GifOutputFields({
  shape,
  fps,
  onFps,
  loop,
  onLoop,
}: {
  shape: GifShape;
  fps: number;
  onFps: (v: number) => void;
  loop: AnimLoop;
  onLoop: (v: AnimLoop) => void;
}) {
  const blownUp = shape.width > shape.keptPixels.width * 1.01 || shape.height > shape.keptPixels.height * 1.01;
  return (
    <>
      <div className="col-span-2 flex flex-wrap items-end gap-3">
        <Field label="Crop">
          <div className="flex items-center gap-2">
            <Button size="sm" variant={shape.cropOn ? 'primary' : 'secondary'} onClick={() => shape.setCropOn(!shape.cropOn)} data-gif-crop>
              <CropIcon size={14} /> {shape.cropOn ? 'Cropping' : 'Crop'}
            </Button>
            {shape.cropOn ? (
              <>
                <Select value={shape.shapeId} onChange={(e) => shape.setShape(e.target.value)} data-gif-crop-shape>
                  {CROP_SHAPES.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </Select>
                <Button size="sm" variant="ghost" onClick={shape.resetCrop}>
                  Reset
                </Button>
              </>
            ) : null}
          </div>
        </Field>
        <Field label="Size (pixels)">
          <div className="flex items-center gap-1.5">
            <SideInput value={shape.width} onCommit={shape.setWidth} label="Width" data-gif-width />
            <span className="text-muted">×</span>
            <SideInput value={shape.height} onCommit={shape.setHeight} label="Height" data-gif-height />
          </div>
        </Field>
        <div className="flex gap-1 pb-1">
          {GIF_SIZES.map((s) => (
            <button key={s} type="button" onClick={() => shape.setShortSide(s)} className="rounded-md border border-line px-1.5 py-0.5 text-[11px] text-muted hover:text-fg">
              {s}p
            </button>
          ))}
        </div>
      </div>
      {blownUp ? (
        <p className="col-span-2 -mt-2 text-xs text-warn">
          Larger than the {shape.cropOn ? 'cropped part' : 'source'} ({shape.keptPixels.width}×{shape.keptPixels.height}): the GIF will look soft.
        </p>
      ) : null}
      <Field label="Frame rate">
        <Select className="w-full" value={fps} onChange={(e) => onFps(Number(e.target.value))}>
          {GIF_FPS.map((f) => (
            <option key={f} value={f}>
              {f} fps
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Looping">
        <Select className="w-full" value={loop} onChange={(e) => onLoop(e.target.value as AnimLoop)} data-gif-loop>
          {ANIM_LOOPS.map((l) => (
            <option key={l.id} value={l.id}>
              {l.label}
            </option>
          ))}
        </Select>
      </Field>
    </>
  );
}

/** A width or height: typed freely, applied on Enter or when leaving the field. */
function SideInput({ value, onCommit, label, ...rest }: { value: number; onCommit: (v: number) => void; label: string } & Record<`data-${string}`, unknown>) {
  const [text, setText] = useState<string | null>(null);
  const commit = () => {
    if (text === null) return;
    const v = Number(text);
    setText(null);
    if (Number.isFinite(v) && v > 0) onCommit(v);
  };
  return (
    <div className="w-20">
    <Input
      className="tabular-nums"
      inputMode="numeric"
      aria-label={label}
      value={text ?? String(value)}
      onChange={(e) => setText(e.target.value.replace(/[^\d]/g, ''))}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && commit()}
      {...rest}
    />
    </div>
  );
}

/** The GIF's encode settings: looping and crop on top of the GIF preset. */
export function gifEncode(loop: AnimLoop, shape: GifShape): EncodeSettings {
  return { ...GIF, ...(loop !== 'forever' ? { animLoop: loop } : {}), ...(shape.crop ? { crop: shape.crop } : {}) };
}

export type NameMode = 'stamp' | 'custom';

/** Name the GIF: the date-stamped name, or one typed in full. */
export function GifNameField({
  mode,
  onMode,
  custom,
  onCustom,
  stampName,
}: {
  mode: NameMode;
  onMode: (m: NameMode) => void;
  custom: string;
  onCustom: (v: string) => void;
  /** The date-stamped name, without extension. */
  stampName: string;
}) {
  return (
    <Field label="File name">
      <div className="space-y-2">
        <div className="flex gap-1 rounded-lg bg-panel-2 p-1 text-xs" role="radiogroup" data-gif-name-mode>
          {(['stamp', 'custom'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              onClick={() => onMode(m)}
              data-gif-name-mode-option={m}
              className={cx('flex-1 rounded-md px-2 py-1.5', mode === m ? 'bg-panel-3 text-fg' : 'text-muted hover:text-fg')}
            >
              {m === 'stamp' ? 'Date stamp' : 'My own name'}
            </button>
          ))}
        </div>
        {mode === 'stamp' ? (
          <div className="truncate rounded-lg border border-line bg-panel-2 px-3 py-2 text-sm text-muted" title={`${stampName}.gif`}>
            {stampName}.gif
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <Input value={custom} maxLength={200} autoFocus placeholder="Name the GIF" onChange={(e) => onCustom(e.target.value)} data-gif-name />
            <span className="text-sm text-muted">.gif</span>
          </div>
        )}
      </div>
    </Field>
  );
}
